import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

function toSectionId(title, id) {
  if (id) return id;
  return `sec-${String(title)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48)}`;
}

function expandSection(el) {
  if (!el) return;
  const summary = el.querySelector(".MuiAccordionSummary-root");
  if (summary && !el.classList.contains("Mui-expanded")) {
    summary.click();
  }
}

export function EditorFormNav({ sections = [] }) {
  if (!sections.length) return null;

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    expandSection(el);
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        mb: 2,
        position: "sticky",
        top: 8,
        zIndex: 10,
        bgcolor: "background.paper",
        boxShadow: 1,
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Navegue pelas seções — clique para ir direto ao bloco
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {sections.map((s) => (
          <Chip
            key={s.id}
            label={s.label}
            size="small"
            clickable
            variant="outlined"
            onClick={() => scrollTo(s.id)}
          />
        ))}
      </Box>
    </Paper>
  );
}

export function EditorSection({
  id,
  title,
  subtitle,
  badge,
  defaultExpanded = false,
  optional = false,
  children,
}) {
  const sectionId = toSectionId(title, id);

  return (
    <Accordion
      id={sectionId}
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        mb: 1.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "8px !important",
        overflow: "hidden",
        "&:before": { display: "none" },
        scrollMarginTop: 72,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRoundedIcon />}
        sx={{
          px: 2,
          py: 0.5,
          bgcolor: defaultExpanded ? "primary.50" : "grey.50",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Box sx={{ pr: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {title}
            </Typography>
            {badge != null && badge !== "" ? (
              <Chip size="small" label={badge} color="primary" variant="outlined" />
            ) : null}
            {optional ? <Chip size="small" label="Opcional" variant="outlined" /> : null}
          </Box>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 2, pb: 2.5, borderTop: "1px solid", borderColor: "divider" }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

export function EditorSubsection({ title, description, optional = false, children }) {
  return (
    <Box
      sx={{
        mb: 2.5,
        p: 2,
        bgcolor: "background.paper",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        borderLeftWidth: 3,
        borderLeftColor: optional ? "grey.400" : "primary.main",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: description ? 0.5 : 1.5 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          {title}
        </Typography>
        {optional ? <Chip size="small" label="Opcional" variant="outlined" /> : null}
      </Box>
      {description ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          {description}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
}

export function EditorListItem({
  index,
  title,
  summary,
  onDelete,
  defaultExpanded = false,
  children,
}) {
  const heading = title || `Item ${index + 1}`;

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        mb: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "6px !important",
        overflow: "hidden",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRoundedIcon fontSize="small" />}
        sx={{ px: 1.5, minHeight: 48, bgcolor: "grey.50" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", width: "100%", pr: 0.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {heading}
            </Typography>
            {summary ? (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {summary}
              </Typography>
            ) : null}
          </Box>
          {onDelete ? (
            <IconButton
              size="small"
              aria-label="Remover"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pt: 1, pb: 2, borderTop: "1px solid", borderColor: "divider" }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

export function EditorListHeader({ title, description, onAdd, addLabel = "Adicionar" }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 1.5 }}>
      <Box>
        {title ? (
          <Typography variant="body2" fontWeight={600}>
            {title}
          </Typography>
        ) : null}
        {description ? (
          <Typography variant="caption" color="text.secondary" display="block">
            {description}
          </Typography>
        ) : null}
      </Box>
      {onAdd ? (
        <Chip label={addLabel} size="small" color="primary" onClick={onAdd} clickable />
      ) : null}
    </Box>
  );
}
